/**
 * TemplateImageBackfill — one-click admin utility to copy the two Documint template image
 * fields (PDF Insert "Vicinity Map", Project "Project Image") from Airtable into Supabase
 * Storage (api/admin/backfill-template-images.js). Loops the batched endpoint by Airtable page
 * cursor and shows running progress. Idempotent — re-running skips already-migrated records.
 *
 * One-off maintenance utility — safe to remove once every project/insert has been migrated.
 */
import { useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'

const ENTITIES = [
  { key: 'pdf_insert', label: 'Vicinity Map (PDF Inserts)' },
  { key: 'project',    label: 'Project Image (Projects)' },
]

export default function TemplateImageBackfill({ dark }) {
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState({})     // { [entityKey]: { total, migrated } }
  const [progress, setProgress] = useState({}) // { [entityKey]: { processed, uploaded, skipped, noImage, failed } }
  const [err, setErr] = useState(null)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'

  async function token() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

  async function check(entityKey) {
    setBusy(true); setErr(null)
    try {
      const res = await fetch('/api/admin/backfill-template-images?entity=' + entityKey, { headers: { Authorization: 'Bearer ' + await token() } })
      const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
      setStatus(s => ({ ...s, [entityKey]: d }))
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function run(entityKey) {
    if (!(await confirmDialog({
      title: 'Copy images from Airtable?',
      message: 'Download every historical attachment for this field from Airtable and store it in Supabase. Safe to re-run — records that already have an image are skipped.',
      confirmLabel: 'Run backfill',
    }))) return
    setBusy(true); setErr(null)
    const totals = { processed: 0, uploaded: 0, skipped: 0, noImage: 0, failed: 0 }
    try {
      let cursor = null, done = false, guard = 0
      while (!done && guard++ < 300) {
        const t = await token()
        const res = await fetch('/api/admin/backfill-template-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t },
          body: JSON.stringify({ entity: entityKey, cursor, limit: 10 }),
        })
        const d = await res.json(); if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status))
        totals.processed += d.processed; totals.uploaded += d.uploaded; totals.skipped += d.skipped
        totals.noImage += d.noImage; totals.failed += d.failed
        setProgress(p => ({ ...p, [entityKey]: { ...totals } }))
        cursor = d.nextCursor; done = d.done
      }
      check(entityKey)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = (primary) => ({
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: primary ? '#1f9d6b' : 'none', color: primary ? '#fff' : textPrim,
    border: primary ? 'none' : `0.5px solid ${border}`,
  })

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 680 }}>
        Copies the historical attachments for the Vicinity Map (Title Sheet PDF Inserts) and Project
        Image (Projects) fields into Supabase Storage. Once migrated, the app edits these fields via
        Supabase — Airtable is no longer read for them (new uploads still mirror back into Airtable so
        the Documint templates keep working). Idempotent — re-run any time; already-migrated records
        are skipped. One-off maintenance utility.
      </div>

      {ENTITIES.map(({ key, label }) => (
        <div key={key} style={{ marginBottom: 18, paddingBottom: 14, borderBottom: `0.5px solid ${border}` }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: textPrim, marginBottom: 8 }}>{label}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button style={btn(false)} disabled={busy} onClick={() => check(key)}>{busy ? 'Working…' : 'Check status'}</button>
            <button style={btn(true)} disabled={busy} onClick={() => run(key)}>{busy ? 'Working…' : 'Run backfill'}</button>
          </div>
          {status[key] && !progress[key] && (
            <div style={{ marginTop: 10, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
              {status[key].total} record(s) with an image · {status[key].migrated} already migrated
            </div>
          )}
          {progress[key] && (
            <div style={{ marginTop: 10, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
              {busy ? 'Working… ' : '✓ Done. '}
              processed {progress[key].processed} · uploaded {progress[key].uploaded} · skipped {progress[key].skipped} · no image {progress[key].noImage}
              {progress[key].failed ? ` · failed ${progress[key].failed}` : ''}
            </div>
          )}
        </div>
      ))}

      {err && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{err}</div>}
    </div>
  )
}
